/**
 * =============================================================================
 * COOKIE BANNER - Banner de Consentimento de Cookies
 * =============================================================================
 *
 * Aparece na parte inferior do ecrã na primeira visita (ou quando o
 * consentimento expira/fica desatualizado). Permite aceitar todos,
 * rejeitar não essenciais ou personalizar preferências.
 *
 * Acessível: role="dialog", aria-live, foco visível, contraste adequado.
 */

import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useCookies } from "@/hooks/useCookies";
import { ROUTES } from "@/constants/routes";

export function CookieBanner() {
  const {
    isBannerOpen,
    isLoading,
    t,
    acceptAll,
    rejectNonEssential,
    openPreferences,
    closeBanner,
  } = useCookies();

  if (isLoading || !isBannerOpen) return null;

  return (
    <AnimatePresence>
      {isBannerOpen && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          role="dialog"
          aria-live="polite"
          aria-label={t.bannerTitle}
          className="fixed bottom-0 left-0 right-0 z-[100] bg-deepOcean/95 text-pureWhite shadow-2xl backdrop-blur-md border-t border-pureWhite/10"
        >
          <div className="container-main py-5 md:py-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-5 lg:gap-8">
              {/* Ícone e texto */}
              <div className="flex items-start gap-4 flex-1">
                <div
                  className="hidden sm:flex w-12 h-12 rounded-full bg-oceanBlue/20 items-center justify-center flex-shrink-0"
                  aria-hidden="true"
                >
                  <Cookie size={24} className="text-softSky" />
                </div>
                <div className="flex-1">
                  <h2 className="font-playfair text-lg md:text-xl font-semibold text-pureWhite mb-2">
                    {t.bannerTitle}
                  </h2>
                  <p className="text-sm text-pureWhite/80 leading-relaxed max-w-3xl">
                    {t.bannerDescription}
                  </p>
                  <Link
                    to={ROUTES.privacidade}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 text-xs text-softSky underline-offset-4 hover:underline transition-colors"
                  >
                    {t.privacyPolicy}
                  </Link>
                </div>
              </div>

              {/* Botões */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                <button
                  onClick={acceptAll}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-oceanBlue text-pureWhite text-sm font-medium transition-all duration-200 hover:bg-softSky hover:shadow-md focus:outline-none focus:ring-2 focus:ring-softSky focus:ring-offset-2 focus:ring-offset-deepOcean"
                >
                  {t.acceptAll}
                </button>

                <button
                  onClick={rejectNonEssential}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-transparent border border-pureWhite/30 text-pureWhite text-sm font-medium transition-all duration-200 hover:bg-pureWhite/10 hover:border-pureWhite/50 focus:outline-none focus:ring-2 focus:ring-pureWhite/50 focus:ring-offset-2 focus:ring-offset-deepOcean"
                >
                  {t.rejectNonEssential}
                </button>

                <button
                  onClick={openPreferences}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-pureWhite/90 text-sm font-medium underline-offset-4 hover:underline transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pureWhite/50 focus:ring-offset-2 focus:ring-offset-deepOcean"
                >
                  {t.customize}
                </button>

                <button
                  onClick={closeBanner}
                  className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-full text-pureWhite/70 hover:bg-pureWhite/10 hover:text-pureWhite transition-colors focus:outline-none focus:ring-2 focus:ring-pureWhite/50 focus:ring-offset-2 focus:ring-offset-deepOcean"
                  aria-label={t.close}
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CookieBanner;
